import message from 'antd/lib/message';
import Spin from 'antd/lib/spin';
import Tabs from 'antd/lib/tabs';
import * as React from 'react';
import { useState, useEffect, useContext } from 'react';
import { fetchBids } from 'src/api/shipmentExecution';
import { allBidsToQuotes, getVendorTypeFromQuoteField, selectedBidsToQuotes } from '../../../SE.constants';
import { RfqBidType } from '../../../SE.interfaces';
import { SEQuoteCandidatesContext, SEQuoteSelectionContext } from '../SEQuotesComponents/SEQuoteSelectionContext';
import RfqBids from './RfqBids';

const TabPane = Tabs.TabPane;

interface RfqBidContainerTabs {

};

const RfqBidContainerTabs = (props) => {
  const { field } = props;
  const [isFetchingBids, setIsFetchingBids] = useState<boolean>(false);
  // const [bids, setBids] = useState<Record<string, RfqBidType[]>>();
  const [bids, setBids] = useState<any>({});
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [selectedBids, setSelectedBids] = useState<Record<string, RfqBidType[] | undefined> | undefined>(undefined); // Object with one bid(value) in each type(key)
  const [selectedBidId, setSelectedBidId] = useState<string | undefined>(undefined);
  const [bidSelectionReason, setBidSelectionReason] = useState<string | undefined>();
  const [primaryContainerSizeType, setPrimaryContainerSizeType] = useState<string>("");
  const [isInitialAssignment, setIsInitialAssignment] = useState<boolean>(true);
  const { shipmentExecutionId } = props.field?.extraData || {};

  // SMT71 Quote Selection: report this vendor's bid pick to the task-level footer so its amount +
  // deviation feed the combined totals and its tab shows the selected indicator. Vendor-type-first key
  // matches the tab (SEQuoteTaskTabs derives the same key). Outside SMT71 the context default is a
  // no-op, so the standalone bid task is unaffected.
  const { reportSelection } = useContext(SEQuoteSelectionContext);
  const { reportCandidates } = useContext(SEQuoteCandidatesContext);
  const vendorType = getVendorTypeFromQuoteField(field?.extraData);
  const selectionKey = vendorType || field?.extraData?.name || field?.extraData?.fieldId || '';

  // fetching bid list ///////////////////
  const setData = async () => {
    if (!shipmentExecutionId) return;
    setIsFetchingBids(true);

    const response = await fetchBids({ id: shipmentExecutionId });
    if (response.isSuccess) {
      if (!response.data) {
        message.error("Unable to fetch data.");
        return;
      }

      setPrimaryContainerSizeType(response.data?.primaryContainerSizeType);

      setBids({
        ...response?.data?.bids,
      });
      
      setErrorMessage('');
    } else {
      setErrorMessage(response.errorMessage);
    }
    setIsFetchingBids(false);
  };

  useEffect(() => {
    if (field?.disabled) {
      return;
    }
    setData();
  }, [props.field?.extraData?.shipmentExecutionId]);

  // SMT71: snapshot every fetched bid (all ranks, all sizes) to the task modal, which merges them into
  // the Quote Candidates field (SMF427) on submit — mirroring how each quote panel reports its list.
  // Skipped on the read-only approval view, where candidates come from the saved snapshot.
  useEffect(() => {
    if (field?.disabled) return;
    reportCandidates(selectionKey, allBidsToQuotes(bids, vendorType));
  }, [bids]);

  useEffect(() => {
    // A fresh SMT71 FF tab starts with no saved value (field.value === undefined), so every access must
    // be optional-chained — without this the task modal crashes to a blank screen on open.
    if (field?.disabled) {
      setPrimaryContainerSizeType(field.value?.primaryContainerSizeType);
      setBids(field.value?.bids);
    }
    if (field?.value?.bids && isInitialAssignment) {
      setSelectedBids(field.value.bids);
      setBidSelectionReason(field.value.bidSelectionReason);
      setIsInitialAssignment(false);
    }
  }, [field?.value]);

  useEffect(() => {
    if (field) {
      field.setValue({ bids: selectedBids, primaryContainerSizeType, bidSelectionReason });
      setIsInitialAssignment(false);
    }
    reportSelection(selectionKey, selectedBidsToQuotes({ bids: selectedBids }, vendorType));

    if (!selectedBids || !primaryContainerSizeType) {
      return;
    }

  }, [selectedBids, primaryContainerSizeType]);

  useEffect(() => {
    if (field) {
      field.setValue({ bids: selectedBids, primaryContainerSizeType, bidSelectionReason })
    }
  }, [bidSelectionReason]);

  const onBidSelection = (bid: RfqBidType, category: string) => {
    // No L1-deviation reason prompt for rank 2+ bids — the SMT71 task captures deviation justification
    // at task level (combined-totals deviation flow), so the per-bid reason modal is not triggered here.
    setSelectedBids({
      ...selectedBids,
      [category]: [bid],
    });
  }

  const renderSelectionReason = () => {
    return bidSelectionReason && (
      <div style={{ paddingBottom: 28, paddingTop: 10 }}>
        <div>Reason for L1 deviation</div>
        <div
          style={{
            fontWeight: 600,
            marginTop: 5,
            lineHeight: 1.8,
            color: '#111111',
          }}
        >
          {bidSelectionReason}
        </div>
      </div>
    );
  };

  if (isFetchingBids) {
    return <div style={{ width: '120%', textAlign: 'center' }}>
      <Spin />
    </div>;
  }

  const tabTitles = Object.keys(bids || {});

  const index = tabTitles.indexOf(primaryContainerSizeType);

  if (index > -1) {
    tabTitles.splice(index, 1);
    tabTitles.unshift(primaryContainerSizeType);
  }

  const resetBids = () => {
    const emptySelectedBids = {};
    tabTitles.forEach(category => {
      emptySelectedBids[category] = [];
    });

    setSelectedBids(emptySelectedBids);
    setBidSelectionReason(undefined);
  }

  const renderNoBidAvailable = () => {
    return (
      <div style={{ color: '#DF1D1D', fontSize: 12 }}>
        There is no bid available.
      </div>
    )
  }
  return <div style={{ width: '122%' }}>
    {renderSelectionReason()}
    {!tabTitles.length ? renderNoBidAvailable() :
      (<Tabs type="card" style={{ width: 'fit-content' }}>
        {tabTitles.map(category => {
          // Show the full ranked list for the size (no FF/carrier filter) so selecting one bid keeps
          // the others visible — matching the CHA / Surveyor quote panels.
          const bidList = bids?.[category];
          const selectedBid = selectedBids?.[category]?.[0];  // assuming value to be an array with one element
          return (<TabPane key={category} tab={category || 'Bids'}>
            <RfqBids
              selectedBid={selectedBid}
              onBidSelection={(bid) => onBidSelection(bid, category)}
              bidList={bidList}
              category={category}
              resetBids={resetBids}
              {...props}
            />
          </TabPane>);
        })}
      </Tabs>)}
  </div>;
}

export default RfqBidContainerTabs;